import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

const NoRowsOverlay = ({ message, topOffset = 0 }) => (
	<Box
		sx={{
			position: 'absolute',
			top: topOffset,
			left: 0,
			right: 0,
			bottom: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			pointerEvents: 'none',
			zIndex: 4,
		}}
	>
		<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
			{message}
		</Typography>
	</Box>
);

const LOADING_MORE_ROW_ID = '__loading-more-row__';
const LOADING_MORE_FLAG = '__loadingMore';

const IRDataGrid = ({
	rows,
	columns,
	loading,
	loadingMore = false,
	hasMore = true,
	sortModel,
	onSortModelChange,
	onRowsScrollEnd,
	scrollEndThreshold = 80,
	noRowsMessage = 'No records found',
	minHeight = 200,
	maxHeight = 'min(60vh, 600px)',
	fillHeight = false,
	rowHeight = 34,
	getRowHeight,
	columnHeaderHeight = 34,
	dataTestId = 'data-grid',
	getRowId = (row) => row.id,
	getDetailPanelContent,
	getDetailPanelHeight,
	detailPanelExpandedRowIds,
	onDetailPanelExpandedRowIdsChange,
	columnVisibilityModel,
	checkboxSelection = false,
	rowSelectionModel,
	onRowSelectionModelChange,
	onCellClick,
}) => {
	const [loadingStartRowCount, setLoadingStartRowCount] = useState(null);
	const receivedMoreRows =
		loadingStartRowCount !== null && rows.length > loadingStartRowCount;
	const showLoadingMoreRow =
		loadingMore ||
		(loadingStartRowCount !== null && !receivedMoreRows && hasMore);

	useEffect(() => {
		if (loadingMore) {
			setLoadingStartRowCount((current) => current ?? rows.length);
			return undefined;
		}
		if (loadingStartRowCount === null) return undefined;
		if (receivedMoreRows || !hasMore) {
			setLoadingStartRowCount(null);
			return undefined;
		}

		const frame = requestAnimationFrame(() => setLoadingStartRowCount(null));
		return () => cancelAnimationFrame(frame);
	}, [
		hasMore,
		loadingMore,
		loadingStartRowCount,
		receivedMoreRows,
		rows.length,
	]);

	const displayedRows = showLoadingMoreRow
		? [...rows, { [LOADING_MORE_FLAG]: true }]
		: rows;
	const displayedColumns = showLoadingMoreRow
		? columns.map((column) => ({
				...column,
				renderCell: (params) => {
					if (params.row[LOADING_MORE_FLAG]) {
						return (
							<Box
								data-testid='data-grid-loading-more-cell'
								sx={{
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<Skeleton
									data-testid='data-grid-loading-more-skeleton'
									width='70%'
									height={18}
								/>
							</Box>
						);
					}
					return column.renderCell
						? column.renderCell(params)
						: (params.formattedValue ?? params.value);
				},
			}))
		: columns;
	const resolveRowId = (row) =>
		row[LOADING_MORE_FLAG] ? LOADING_MORE_ROW_ID : getRowId(row);

	return (
		<Box sx={{ position: 'relative', height: fillHeight ? '100%' : 'auto' }}>
			<DataGridPremium
				rows={displayedRows}
				columns={displayedColumns}
				loading={loading}
				getRowId={resolveRowId}
				checkboxSelection={checkboxSelection}
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={onRowSelectionModelChange}
				sortingMode='server'
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				onRowsScrollEnd={onRowsScrollEnd}
				scrollEndThreshold={scrollEndThreshold}
				style={{ minHeight, maxHeight }}
				rowHeight={rowHeight}
				getRowHeight={getRowHeight}
				columnHeaderHeight={columnHeaderHeight}
				hideFooter
				disableRowSelectionOnClick
				disableColumnMenu
				disableAggregation
				disableRowGrouping
				getDetailPanelContent={getDetailPanelContent}
				getDetailPanelHeight={getDetailPanelHeight}
				detailPanelExpandedRowIds={detailPanelExpandedRowIds}
				onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
				columnVisibilityModel={columnVisibilityModel}
				onCellClick={onCellClick}
				slots={{ noRowsOverlay: () => null }}
				slotProps={{
					loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
				}}
				data-testid={dataTestId}
				sx={{
					border: 'none',
					fontSize: '12px',
					borderRadius: 0,
					'& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitleContainer':
						{
							backgroundColor: 'background.subtle',
							borderRadius: '0 !important',
						},
					'& .MuiDataGrid-columnHeaderTitle': {
						fontSize: '12px',
						fontWeight: 700,
						letterSpacing: '0.05em',
						color: 'text.secondary',
					},
					'& .MuiDataGrid-cell': {
						borderColor: 'border.light',
					},
					'& .MuiDataGrid-cell[data-field="subject"]': {
						cursor: 'pointer',
					},
					'& .MuiDataGrid-row:hover': {
						backgroundColor: 'background.subtle',
					},
				}}
			/>
			{!loading && displayedRows.length === 0 && noRowsMessage && (
				<NoRowsOverlay message={noRowsMessage} topOffset={columnHeaderHeight} />
			)}
		</Box>
	);
};

NoRowsOverlay.propTypes = {
	message: PropTypes.string.isRequired,
	topOffset: PropTypes.number,
};

IRDataGrid.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	loading: PropTypes.bool,
	loadingMore: PropTypes.bool,
	hasMore: PropTypes.bool,
	sortModel: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string,
			sort: PropTypes.oneOf(['asc', 'desc']),
		})
	),
	onSortModelChange: PropTypes.func,
	onRowsScrollEnd: PropTypes.func,
	scrollEndThreshold: PropTypes.number,
	noRowsMessage: PropTypes.string,
	minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	fillHeight: PropTypes.bool,
	rowHeight: PropTypes.number,
	getRowHeight: PropTypes.func,
	columnHeaderHeight: PropTypes.number,
	dataTestId: PropTypes.string,
	getRowId: PropTypes.func,
	getDetailPanelContent: PropTypes.func,
	getDetailPanelHeight: PropTypes.func,
	detailPanelExpandedRowIds: PropTypes.array,
	onDetailPanelExpandedRowIdsChange: PropTypes.func,
	columnVisibilityModel: PropTypes.object,
	checkboxSelection: PropTypes.bool,
	rowSelectionModel: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	onRowSelectionModelChange: PropTypes.func,
	onCellClick: PropTypes.func,
};

export default IRDataGrid;
